import { useEffect } from 'react';

const SITE_URL = 'https://bharatheal.com';
const DEFAULT_OG_IMAGE = `${SITE_URL}/logo.png`;

/**
 * Set or create a <meta> tag, and return a function that restores whatever was
 * there before.
 *
 * The restore step matters: without it, navigating from a page that sets
 * `robots: noindex` to one that doesn't would leave the noindex tag in the
 * document, quietly de-indexing the next page.
 */
const setMeta = (selectorAttr, selectorValue, content) => {
    const selector = `meta[${selectorAttr}="${selectorValue}"]`;
    let tag = document.head.querySelector(selector);
    const created = !tag;
    const previous = tag ? tag.getAttribute('content') : null;

    if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(selectorAttr, selectorValue);
        document.head.appendChild(tag);
    }
    tag.setAttribute('content', content);

    return () => {
        if (created) {
            tag.remove();
        } else if (previous !== null) {
            tag.setAttribute('content', previous);
        }
    };
};

const setLink = (rel, href) => {
    let tag = document.head.querySelector(`link[rel="${rel}"]`);
    const created = !tag;
    const previous = tag ? tag.getAttribute('href') : null;

    if (!tag) {
        tag = document.createElement('link');
        tag.setAttribute('rel', rel);
        document.head.appendChild(tag);
    }
    tag.setAttribute('href', href);

    return () => {
        if (created) {
            tag.remove();
        } else if (previous !== null) {
            tag.setAttribute('href', previous);
        }
    };
};

/**
 * Manage per-page SEO metadata.
 *
 * @param {Object}  seoData
 * @param {string}  seoData.title        Page title (aim for under 60 chars)
 * @param {string}  seoData.description  Meta description (under 155 chars)
 * @param {string}  seoData.ogImage      Absolute Open Graph image URL
 * @param {string}  seoData.canonical    Canonical path ("/about") or absolute URL
 * @param {string}  seoData.robots       e.g. "noindex, follow"
 * @param {Object}  seoData.jsonLd       Structured data for this page
 */
export const useSEO = ({ title, description, ogImage, canonical, robots, jsonLd }) => {
    // Objects are re-created on every render, so depend on a stable serialisation
    // rather than the reference, or this effect would run in a loop.
    const jsonLdKey = jsonLd ? JSON.stringify(jsonLd) : null;

    useEffect(() => {
        const cleanups = [];
        const previousTitle = document.title;

        if (title) {
            document.title = title;
            cleanups.push(() => { document.title = previousTitle; });
        }

        if (description) {
            cleanups.push(setMeta('name', 'description', description));
            cleanups.push(setMeta('property', 'og:description', description));
            cleanups.push(setMeta('property', 'twitter:description', description));
        }

        if (title) {
            cleanups.push(setMeta('property', 'og:title', title));
            cleanups.push(setMeta('property', 'twitter:title', title));
        }

        cleanups.push(setMeta('property', 'og:image', ogImage || DEFAULT_OG_IMAGE));
        cleanups.push(setMeta('property', 'twitter:image', ogImage || DEFAULT_OG_IMAGE));

        // Default to indexable. Set explicitly so a page that follows a
        // noindex page cannot inherit it.
        cleanups.push(setMeta('name', 'robots', robots || 'index, follow'));

        if (canonical) {
            const href = canonical.startsWith('http') ? canonical : `${SITE_URL}${canonical}`;
            cleanups.push(setLink('canonical', href));
            cleanups.push(setMeta('property', 'og:url', href));
            cleanups.push(setMeta('property', 'twitter:url', href));
        }

        if (jsonLdKey) {
            const script = document.createElement('script');
            script.type = 'application/ld+json';
            script.dataset.seoHook = 'true';
            script.textContent = jsonLdKey;
            document.head.appendChild(script);
            cleanups.push(() => script.remove());
        }

        return () => cleanups.forEach((restore) => restore());
    }, [title, description, ogImage, canonical, robots, jsonLdKey]);
};
